import { Button, Box, Typography, useTheme, alpha } from "@mui/material";
import { departmentsApi, publicDocumentsApi, segmentApi } from "api";
import { DepartmentEntity, PublicDocumentEntity, SegmentEntity, UpdatePublicDocumentDto } from "api/generated";
import { useSeason } from "contexts/SeasonContext";
import AddPublicDocumentModal from "page-sections/data-table/dataTableV2/AddPublicDocumentModal";
import PublicDocumentAccordion from "page-sections/public-document/publicDocumentAccordion";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { checkTokenRole } from "utils/checkToken";
import AddIcon from '@mui/icons-material/Add';
import DescriptionIcon from '@mui/icons-material/Description';


const PublicDocuments: FC = () => {

    const [segments, setSegments] = useState<SegmentEntity[]>([]);
    const [departments, setDepartments] = useState<DepartmentEntity[]>([]);
    const [documents, setDocuments] = useState<PublicDocumentEntity[]>([]);
    const [openModal, setOpenModal] = useState(false);
    const [updatingDocument, setUpdatingDocument] = useState<PublicDocumentEntity>();
    const { seasonId, seasons } = useSeason();

    const { t } = useTranslation();
    const role = checkTokenRole();
    const isManager = role === 'MANAGER' || role === 'OWNER' || role === 'DEVELOPER';

    const fetchSegments = async () => {
        if (role === 'USER' || role === 'DEPCHIEF')
            setSegments((await segmentApi.findByUser(seasonId)).data);
        else
            setSegments((await segmentApi.findAll()).data);
    }


    const fetchDepartments = async () => {
        if (role === 'USER' || role === 'DEPCHIEF')
            setDepartments(((await departmentsApi.findByRole(seasonId)).data))
        else
            setDepartments((await departmentsApi.findAll()).data);
    }

    const fetchData = async () => {
        if (role === 'USER' || role === 'DEPCHIEF')
            setDocuments(((await publicDocumentsApi.findByUser()).data))
        else
            setDocuments(((await publicDocumentsApi.findAll()).data))
    }

    const createDocument = async (data: any, file: File) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('createPublicDocumentDto', JSON.stringify(data))
        await publicDocumentsApi.create({
            data: formData

        });
        await fetchData();
    }

    const updateVisibility = async (data: UpdatePublicDocumentDto) => {
        if (updatingDocument)
            await publicDocumentsApi.update(updatingDocument.id, data);
        await fetchData();
    }

    useEffect(() => {
        fetchData();
        fetchDepartments();
        fetchSegments();
    }, []);

    const openUpdateVisibility = (data: PublicDocumentEntity) => {
        setUpdatingDocument(data);
        setOpenModal(true);
    }


    const theme = useTheme();

    return (
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <AddPublicDocumentModal
                departments={departments}
                segments={segments}
                onClose={() => { setOpenModal(false); setUpdatingDocument(undefined) }}
                onSumbit={createDocument}
                data={updatingDocument}
                onUpdate={updateVisibility}
                open={openModal}
            />

            {/* Header Section */}
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 4,
                flexWrap: 'wrap',
                gap: 2
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <DescriptionIcon sx={{ fontSize: 32, color: theme.palette.primary.main }} />
                    </Box>
                    <Box>
                        <Typography variant="h4" fontWeight="bold" color="text.primary">
                            {t('publicDocuments.title') || 'Public Documents'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {documents.length} {t('publicDocuments.totalDocuments') || 'documents available'}
                        </Typography>
                    </Box>
                </Box>
                
                {isManager && (
                    <Button 
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => { setOpenModal(true) }}
                        sx={{
                            borderRadius: 2,
                            px: 3,
                            py: 1.5,
                            textTransform: 'none',
                            fontWeight: 600,
                            boxShadow: theme.shadows[2],
                            '&:hover': {
                                boxShadow: theme.shadows[4],
                            }
                        }}
                    >
                        {t('employees.contract.contractFile')}
                    </Button>
                )}
            </Box>

            {/* Documents Section */}
            <Box>
                <PublicDocumentAccordion
                    departments={departments}
                    documents={documents}
                    seasons={seasons}
                    segments={segments}
                    isManager={isManager}
                    fetchDataCallBack={fetchData}
                    updateCallBack={openUpdateVisibility}
                />
            </Box>
        </Box>
    )
}

export default PublicDocuments;